import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react'
import c3, { ChartAPI, ChartConfiguration } from 'c3'
import { HostData } from '../Customer/Data'
import {
  CheckLineIcon,
  CloseLineIcon,
  HighlightsSectionIcon,
  LockLineIcon,
  PaperDocumentIcon,
  SecurityOfficerIcon,
} from '../Icons/Icons'
import { BRIEF_DONUT_COLOURS } from './BriefDonut'
import { BriefShell } from './BriefShell'
import { KeyInsights } from './KeyInsights'
import { useCustomerProfile } from './useCustomerProfile'
import { useChartResize } from '../Visualizations/useChartResize'
import '../Visualizations/chartTooltip.css'
import styles from './BriefShell.module.css'
import demoStyles from './SecurityDemoBrief.module.css'

/*
 * DEMO ONLY: a mock of what the Security brief could look like, for the developers and for demos. Every number in
 * it is made up and nothing is fetched (other than the customer's industry for the report details): it is built
 * from the same pieces as the Health check and Fleet makeup (the headline stub, key insights, sections of metric
 * tiles, donuts and tables) and covers the data the Security brief could draw on, in its three groups: exposure
 * (version and platform), access control and document protection. It has no notes and isn't wired to the AI
 * assistant. It is separate from the real Security brief (SecurityBrief.tsx, at briefs/security) and touches
 * none of it: it lives at briefs/security-demo, under Trial on the Briefs tab.
 */

interface Props {
  teamID: string
  host: HostData
}

/* how serious a finding is (none: nothing to raise) */
type Severity = 'High' | 'Medium' | 'Low'
/* low to high, left to right, everywhere they're shown side by side (the chart, its legend and the summary) */
const SEVERITY_ORDER: Severity[] = ['Low', 'Medium', 'High']

/* what a finding puts at risk, and how to put it right (for every high and medium one) */
interface Remedy {
  risk: string
  /** how to put it right, step by step, each led by what it does */
  mitigations: { lead: string; text: string }[]
}

interface DemoTile {
  label: string
  value: string
  subtext?: string
  /** a finding worth raising (high and medium turn the tile's icon red) */
  severity?: Severity
  remedy?: Remedy
}

interface DemoDonut {
  key: string
  label: string
  title: string
  segments: [string, number][]
}

interface DemoRow {
  name: string
  value: string
  detail: string
  severity?: Severity
  remedy?: Remedy
}

/* ---------- the made-up findings ---------- */

/* as the designer's prototype has them: each led by what kind of point it is */
const INSIGHTS = [
  'High severity: raise the 5 high-severity findings with the customer first. MF is three releases behind with a critical advisory, its admin console can be reached from the internet, its database is out of support and 4 queues print without checking who sent the job.',
  'Quick fixes: spool file encryption, the Web Print sandbox and a CA-signed certificate are settings changes that need no new hardware.',
  'Additional value: only 41% of printing goes through secure release. Extending hold/release to all 28 queues closes the biggest gap in document protection.',
  'Timing: support and licence end in 47 days, a natural moment to propose the upgrade alongside a security review.',
]

/* where the customer's security set-up sits, as a percentile, among all PaperCut customers and among those in its
   own industry (scored on the findings below, weighted by severity) */
const BENCHMARK = { all: 75, industry: 81 }

const EXPOSURE_TILES: DemoTile[] = [
  {
    label: 'PaperCut MF version',
    value: '22.1.4',
    subtext: '3 releases behind 24.1.2',
    severity: 'High',
    remedy: {
      risk:
        'Older releases carry security flaws that have since been fixed and published, so anyone can look up how to exploit them. 22.1.4 is affected by both open advisories below.',
      mitigations: [
        {
          lead: 'Back up first',
          text: 'Take a backup of the PaperCut server and its database, so the upgrade can be rolled back.',
        },
        {
          lead: 'Upgrade in order',
          text: 'Upgrade the application server to 24.1.2, then the site servers and secondary servers.',
        },
        {
          lead: 'Schedule downtime',
          text: 'Plan about an hour out of hours, and tell users that printing will pause while it runs.',
        },
      ],
    },
  },
  {
    label: 'Admin console reachable from the internet',
    value: 'Yes',
    subtext: 'Yes: its login page answers from outside the network',
    severity: 'High',
    remedy: {
      risk:
        'An admin login that answers from the internet can be targeted by password guessing and by exploits of known flaws. Taking over the admin console gives control of every queue, user and document setting.',
      mitigations: [
        {
          lead: 'Close it to the internet',
          text: 'Block ports 9191 and 9192 at the firewall to all traffic from outside the network.',
        },
        {
          lead: 'Restrict admin access',
          text:
            'Allow the admin console only from the office network or a VPN, using admin IP restrictions under Options > Advanced > Security.',
        },
        {
          lead: 'Review admin accounts',
          text: 'Remove admin accounts that are no longer needed, and turn on two-step verification for the rest.',
        },
      ],
    },
  },
  {
    label: 'Support and licence',
    value: '47 days',
    subtext: 'Until they expire on 18 Dec 2026',
    severity: 'Medium',
    remedy: {
      risk:
        'Once support and maintenance lapse, the customer can no longer upgrade to releases that fix security flaws, and gets no help if they are attacked.',
      mitigations: [
        {
          lead: 'Renew before it lapses',
          text: 'Renew support and maintenance before 18 Dec 2026, so upgrades and security releases stay available.',
        },
        {
          lead: 'Pair it with the upgrade',
          text:
            'Propose the renewal together with the 24.1.2 upgrade, so the customer moves to a supported release in one step.',
        },
      ],
    },
  },
  {
    label: 'End-of-life devices',
    value: '6',
    subtext: 'Of 42 no longer get firmware fixes',
    severity: 'Medium',
    remedy: {
      risk:
        'Devices that no longer get firmware updates keep any flaws found in them. They are also the ones most likely to be on default admin passwords and old protocols.',
      mitigations: [
        { lead: 'Plan replacements', text: 'Replace the 6 end-of-life devices in the next fleet refresh.' },
        {
          lead: 'Harden them meanwhile',
          text: "Change their default admin passwords and turn off protocols they don't need (FTP, Telnet, SNMP v1).",
        },
        { lead: 'Isolate them', text: 'Move them to a separate network segment that only the print server can reach.' },
      ],
    },
  },
  {
    label: 'Known security advisories',
    value: '2',
    subtext: 'PC-2026-04 (critical), PC-2026-07 (high)',
    severity: 'High',
    remedy: {
      risk:
        'PC-2026-04 lets an attacker on the network run code on the PaperCut server without signing in. PC-2026-07 can expose user details. Both are publicly known.',
      mitigations: [
        { lead: 'Upgrade', text: 'Upgrading to PaperCut MF 24.1.2 fixes both advisories.' },
        {
          lead: 'Apply the workaround now',
          text: "If the upgrade has to wait, apply the workaround in PaperCut's advisory for PC-2026-04 straight away.",
        },
        {
          lead: 'Watch for new advisories',
          text:
            "Subscribe the customer's admins to PaperCut security notifications, so they hear about the next one first.",
        },
      ],
    },
  },
  { label: 'Last reported in', value: '2 hours ago', subtext: 'Call-home is up to date' },
]

const PLATFORM: DemoRow[] = [
  {
    name: 'Server operating system',
    value: 'Windows Server 2016',
    detail: 'Extended support ends Jan 2027',
    severity: 'Medium',
    remedy: {
      risk:
        'After January 2027 Microsoft stops publishing security fixes for Windows Server 2016, so new flaws in it will stay open.',
      mitigations: [
        {
          lead: 'Plan the move',
          text: 'Move the PaperCut server to Windows Server 2022 before January 2027; PaperCut MF 24.1 supports it.',
        },
        {
          lead: 'Do it with the upgrade',
          text: 'Combine the move with the PaperCut upgrade, so there is one maintenance window, not two.',
        },
      ],
    },
  },
  {
    name: 'Database',
    value: 'Microsoft SQL Server 2014',
    detail: 'Out of support since Jul 2024',
    severity: 'High',
    remedy: {
      risk:
        'SQL Server 2014 has had no security fixes since July 2024. The database holds every user, account balance and job record.',
      mitigations: [
        { lead: 'Move to a supported database', text: 'Move to SQL Server 2019 or 2022, or Azure SQL.' },
        {
          lead: 'Keep the history',
          text: "Use PaperCut's database export and import, so users, balances and job history carry across.",
        },
        { lead: 'Lock it down', text: 'Give PaperCut its own database login with only the access it needs.' },
      ],
    },
  },
  { name: 'Java runtime', value: '11.0.18 (bundled)', detail: 'Current for this release' },
  {
    name: 'TLS certificate',
    value: 'Self-signed',
    detail: 'Browsers warn users; a CA-signed one is advised',
    severity: 'Medium',
    remedy: {
      risk:
        'Users learn to click through certificate warnings, which makes it easy for a look-alike page to collect their passwords.',
      mitigations: [
        {
          lead: 'Get a signed certificate',
          text: "Request a certificate from the customer's own certificate authority, or a public one.",
        },
        {
          lead: 'Install it',
          text: 'Install it on the PaperCut server by following the SSL/TLS key steps in the PaperCut manual.',
        },
        { lead: 'Retire the old one', text: 'Remove the self-signed certificate so browsers stop showing warnings.' },
      ],
    },
  },
]

const ACCESS_TILES: DemoTile[] = [
  {
    label: 'Single sign-on',
    value: 'Off',
    subtext: 'Off: users keep separate PaperCut credentials',
    severity: 'Medium',
    remedy: {
      risk:
        'A separate PaperCut password is one more to forget, reuse or write down, and it stays active when someone leaves until it is removed by hand.',
      mitigations: [
        {
          lead: 'Connect their identity provider',
          text:
            "Turn on single sign-on with Microsoft Entra ID, Google or Okta, so PaperCut sign-in follows the customer's own accounts and policies.",
        },
        {
          lead: 'Remove local accounts',
          text: 'Retire the 14 local accounts once everyone signs in through the identity provider.',
        },
      ],
    },
  },
  { label: 'User directory', value: 'Active Directory', subtext: 'Plus 14 local accounts' },
  {
    label: 'Unauthenticated print queues',
    value: '4',
    subtext: "Of 28 trust the sender's username unchecked",
    severity: 'High',
    remedy: {
      risk:
        'Anyone on the network can send jobs to these queues as someone else, charging their account and getting round print rules, with nothing in the logs to say who really printed.',
      mitigations: [
        {
          lead: 'Check who sent each job',
          text: 'Turn off the unauthenticated printer setting on the 4 queues, so PaperCut verifies the sender.',
        },
        {
          lead: 'Use secure release',
          text: 'Or route the queues through hold/release, so users sign in at the device to collect their jobs.',
        },
      ],
    },
  },
  {
    label: 'Restrictions in use',
    value: '37',
    subtext: '12 users, 3 groups, 22 shared accounts',
    severity: 'Low',
  },
]

/* how devices sign users in (a bar chart) */
const DEVICE_SIGN_IN: [string, number][] = [
  ['Swipe card', 18],
  ['Username and password', 14],
  ['PIN', 6],
  ['ID number', 3],
  ['Anonymous', 1],
]

/* held pages never released, as a share of all held pages, month by month (a line chart) */
const ABANDONED_TREND: [string, number][] = [
  ['Nov', 11],
  ['Dec', 12],
  ['Jan', 12],
  ['Feb', 13],
  ['Mar', 14],
  ['Apr', 13],
  ['May', 15],
  ['Jun', 16],
  ['Jul', 15],
  ['Aug', 17],
  ['Sep', 18],
  ['Oct', 18],
]

const DOCUMENT_DONUTS: DemoDonut[] = [
  {
    key: 'release-coverage',
    label: 'Queues that hold jobs for secure release',
    title: '43%',
    segments: [
      ['Hold/release', 12],
      ['Print directly', 16],
    ],
  },
  {
    key: 'abandoned-jobs',
    label: 'Held pages never released',
    title: '18%',
    segments: [
      ['Released', 4120],
      ['Abandoned', 905],
    ],
  },
]

const DOCUMENT_CONTROLS: DemoRow[] = [
  {
    name: 'Secure print release',
    value: 'On',
    detail: 'Only 41% of printing goes through release',
    severity: 'Medium',
    remedy: {
      risk:
        'The rest prints straight away, so confidential pages sit in output trays where anyone walking past can pick them up.',
      mitigations: [
        { lead: 'Extend secure release', text: 'Turn on hold/release for the 16 queues that print directly.' },
        {
          lead: 'Start with sensitive areas',
          text: 'Begin with the queues near HR, finance and reception, where confidential pages are most likely.',
        },
        {
          lead: 'Clear unreleased jobs',
          text: 'Set held jobs to be deleted after a few hours, so abandoned prints never come out.',
        },
      ],
    },
  },
  {
    name: 'Spool file encryption',
    value: 'Off',
    detail: 'Queued print files sit unencrypted on disk',
    severity: 'Medium',
    remedy: {
      risk:
        'Held jobs wait on the print server as plain files, readable by anyone who gets into the server or its backups.',
      mitigations: [
        {
          lead: 'Encrypt held jobs',
          text:
            "Turn on spool file encryption in the print provider settings. It's a settings change with no new hardware.",
        },
        {
          lead: 'Check the backups',
          text: "Make sure server backups don't keep unencrypted copies of the spool folder.",
        },
      ],
    },
  },
  { name: 'Watermarking', value: 'On', detail: '5 of 28 queues', severity: 'Low' },
  { name: 'Print archiving', value: 'On', detail: '3 queues keep a copy of every job' },
  {
    name: 'Web Print',
    value: 'On',
    detail: 'Converts uploads outside the recommended sandbox',
    severity: 'Medium',
    remedy: {
      risk: 'Uploaded files are opened on the PaperCut server itself, so a malicious document could reach the server.',
      mitigations: [
        {
          lead: 'Sandbox conversions',
          text: 'Move Web Print conversion to a separate sandbox server, as PaperCut recommends.',
        },
        { lead: 'Limit file types', text: 'Allow only the file types users need to upload.' },
      ],
    },
  },
  { name: 'Scan destinations', value: 'Email, cloud, folder', detail: 'Scanning on 9 devices', severity: 'Low' },
  { name: 'Print scripting', value: 'On', detail: '2 queues run custom scripts', severity: 'Low' },
]

/* the brief's three groups of findings, for the category summary and its sections */
const CATEGORIES: { id: string; title: string; findings: { severity?: Severity }[] }[] = [
  { id: 'security-demo-exposure', title: 'Version and platform', findings: [...EXPOSURE_TILES, ...PLATFORM] },
  { id: 'security-demo-access', title: 'Access control', findings: ACCESS_TILES },
  { id: 'security-demo-documents', title: 'Document protection', findings: DOCUMENT_CONTROLS },
]

const countOf = (findings: { severity?: Severity }[], severity: Severity) =>
  findings.filter(finding => finding.severity === severity).length

const ALL_FINDINGS = CATEGORIES.flatMap(category => category.findings)

/* the severities' colours, in the brief's own reds, ambers and blues */
const SEVERITY_COLOURS: Record<Severity, string> = { High: '#b0334b', Medium: '#f2b33d', Low: '#2c52bb' }

/* ---------- the pieces, as in the Health check and Fleet makeup ---------- */

/* ---------- the charts: kept simple, as pictures: no axes or gridlines, each value in its legend (as the donuts
   have theirs) and in its tooltip ---------- */

const CHART_HEIGHT = 220

/* a c3 chart, bare: no axes or gridlines. With its legend on the right, it notes how wide that legend is (as
   --legend-width, on its figure) so the heading under it can centre on the chart itself */
const BareChart: FunctionComponent<{ label: string; config: ChartConfiguration }> = ({ label, config }) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<ChartAPI | null>(null)
  useChartResize(elementRef, chartRef, true, CHART_HEIGHT)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return undefined
    const chart = c3.generate({
      bindto: element,
      size: { height: CHART_HEIGHT },
      legend: { show: false },
      point: { show: false },
      resize: { auto: false }, // useChartResize follows its figure instead
      onrendered: () => {
        if (config.legend?.position !== 'right') return
        const items = Array.from(element.querySelectorAll('.c3-legend-item'))
        if (items.length === 0) return
        const left = Math.min(...items.map(item => item.getBoundingClientRect().left))
        element.parentElement?.style.setProperty(
          '--legend-width',
          `${Math.max(0, element.getBoundingClientRect().right - left)}px`
        )
      },
      ...config,
      axis: {
        ...config.axis,
        x: { show: false, ...config.axis?.x },
        y: { show: false, ...config.axis?.y },
      },
    })
    chartRef.current = chart
    return () => {
      chartRef.current = null
      chart.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the charts' configs are fixed
  }, [])

  return <div ref={elementRef} className={`${demoStyles.chart} chart-tooltip`} role='img' aria-label={label} />
}

/* c3's own legend on the right, as the brief's donuts have it */
const LEGEND_RIGHT: ChartConfiguration['legend'] = { show: true, position: 'right' }

/* a chart filling its place, its heading centred under the chart (not the legend beside it) */
const ChartFigure: FunctionComponent<{ title: string }> = ({ title, children }) => (
  <figure className={demoStyles.chartFigure}>
    {children}
    <figcaption>
      <h5 className='no-margin-bottom'>{title}</h5>
    </figcaption>
  </figure>
)

/* a bar's tooltip beside its own bar (to its right, or above it when the bars stand up), not at the middle of
   the chart: every bar here shares one category, so c3 would otherwise put each tooltip in the same place */
const besideBar = (rotated: boolean): NonNullable<ChartConfiguration['tooltip']>['position'] =>
  // c3 calls this with its chart internals as `this`, and the hovered bar's data (typed loosely by c3)
  // eslint-disable-next-line func-names
  function(data, width, height) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- c3's internals aren't typed
    const chart = this as any
    const [d] = (data as unknown) as { id: string; index: number }[]
    const bar: SVGGraphicsElement | null =
      d && chart.main?.select(`.c3-target${chart.getTargetSelectorSuffix(d.id)} .c3-bar-${d.index}`).node()
    if (!bar) return { top: 0, left: 0 }
    const box = bar.getBBox()
    const { top, left } = chart.margin as { top: number; left: number }
    return rotated
      ? { top: top + box.y + box.height / 2 - height / 2, left: left + box.x + box.width + 8 }
      : { top: top + box.y - height - 8, left: left + box.x + box.width / 2 - width / 2 }
  }

/* bars side by side (or stacked down, `rotated`), each its own series so each takes its own colour and legend
   entry */
const barsConfig = (bars: [string, number][], colours: string[], rotated = false): ChartConfiguration => ({
  data: {
    columns: bars.map(([name, value]) => [name, value]),
    type: 'bar',
    colors: Object.fromEntries(bars.map(([name], i) => [name, colours[i % colours.length]])),
    order: null,
  },
  bar: { width: { ratio: 0.9 } },
  axis: { rotated, x: { type: 'category', categories: [''] }, y: { min: 0, padding: { bottom: 0 } } },
  legend: LEGEND_RIGHT,
  tooltip: { grouped: false, format: { title: () => '' }, position: besideBar(rotated) },
})

/* c3 lays its inset legend out in equal-width slots, so a short name (Low) leaves a wider gap after it: this sets
   the items out again with the same gap between each, keeping the legend's right edge where it was */
const LEGEND_GAP = 24

// c3 calls this with its chart internals as `this`
// eslint-disable-next-line func-names
const spaceLegendEvenly = function(this: unknown) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- c3's internals aren't typed
  const svg: SVGSVGElement | undefined = (this as any)?.svg?.node()
  const items = Array.from(svg?.querySelectorAll<SVGGElement>('.c3-legend-item') ?? [])
  // each item's drawn extent (its square and name, not the slot around them), before any shift of ours
  const extents = items.map(item => {
    const tile = item.querySelector<SVGGraphicsElement>('.c3-legend-item-tile')?.getBBox()
    const text = item.querySelector<SVGGraphicsElement>('text')?.getBBox()
    const start = Math.min(tile?.x ?? Infinity, text?.x ?? Infinity)
    return { start, end: text ? text.x + text.width : start }
  })
  if (items.length === 0 || extents.some(extent => !Number.isFinite(extent.start))) return
  const width = extents.reduce((sum, { start, end }) => sum + end - start, 0) + LEGEND_GAP * (items.length - 1)
  let x = Math.max(...extents.map(({ end }) => end)) - width
  items.forEach((item, i) => {
    item.setAttribute('transform', `translate(${x - extents[i].start}, 0)`)
    x += extents[i].end - extents[i].start + LEGEND_GAP
  })
}

/* the findings by severity, low to high: one wide bar each, edge to edge, its count large under it, and the
   severities named in c3's own legend, top right */

const SeverityChart: FunctionComponent = () => {
  const counts = SEVERITY_ORDER.map(severity => countOf(ALL_FINDINGS, severity))
  return (
    <figure className={demoStyles.severityChart}>
      <figcaption>
        <h4 className='no-margin-bottom'>Security issues</h4>
      </figcaption>
      <BareChart
        label={`Security issues: ${SEVERITY_ORDER.map((s, i) => `${counts[i]} ${s.toLowerCase()}`).join(', ')}`}
        config={{
          // a series per severity, each with its value in its own place, stacked so each bar is full width
          data: {
            columns: SEVERITY_ORDER.map((severity, i) => [
              severity,
              ...counts.map((count, j) => (i === j ? count : null)),
            ]),
            type: 'bar',
            groups: [SEVERITY_ORDER],
            colors: Object.fromEntries(SEVERITY_ORDER.map(severity => [severity, SEVERITY_COLOURS[severity]])),
            order: null,
          },
          bar: { width: { ratio: 1 } },
          axis: { x: { type: 'category', categories: SEVERITY_ORDER } },
          padding: { top: 48 }, // room for the heading and legend above the bars
          legend: {
            show: true,
            position: 'inset',
            inset: { anchor: 'top-right', x: 0, y: -56, step: 1 },
            padding: 16,
          },
          onrendered: spaceLegendEvenly,
          tooltip: { grouped: false, position: besideBar(false) },
        }}
      />
      <div className={demoStyles.severityCounts} aria-hidden='true'>
        {counts.map((count, i) => (
          <span key={SEVERITY_ORDER[i]}>{count}</span>
        ))}
      </div>
    </figure>
  )
}

const SignInChart: FunctionComponent = () => (
  <ChartFigure title='How devices sign users in'>
    <BareChart
      label={`How devices sign users in: ${DEVICE_SIGN_IN.map(([way, count]) => `${way} ${count}`).join(', ')}`}
      config={barsConfig(DEVICE_SIGN_IN, BRIEF_DONUT_COLOURS, true)}
    />
  </ChartFigure>
)

const AbandonedTrendChart: FunctionComponent = () => {
  const [first, last] = [ABANDONED_TREND[0], ABANDONED_TREND[ABANDONED_TREND.length - 1]]
  return (
    <ChartFigure title='Held pages never released, last 12 months'>
      <BareChart
        label={`Held pages never released, rising from ${first[1]}% to ${last[1]}% over 12 months`}
        config={{
          data: {
            columns: [['Never released', ...ABANDONED_TREND.map(([, value]) => value)]],
            type: 'area-spline',
            colors: { 'Never released': SEVERITY_COLOURS.High },
          },
          axis: { x: { padding: { left: 0, right: 0 } }, y: { min: 0, padding: { bottom: 0 } } },
          legend: LEGEND_RIGHT,
          tooltip: {
            format: {
              title: x => ABANDONED_TREND[Number(x)]?.[0] ?? '',
              value: value => `${value}%`,
            },
          },
        }}
      />
    </ChartFigure>
  )
}

/* a donut, as the brief's others are drawn (its total in the middle), filling its place */
const Donut: FunctionComponent<{ donut: DemoDonut }> = ({ donut }) => (
  <ChartFigure title={donut.label}>
    <BareChart
      label={`${donut.label}: ${donut.segments.map(([name, value]) => `${name} ${value}`).join(', ')}`}
      config={{
        data: {
          columns: donut.segments,
          type: 'donut',
          colors: Object.fromEntries(donut.segments.map(([name], i) => [name, BRIEF_DONUT_COLOURS[i]])),
          order: null,
        },
        donut: { title: donut.title, label: { show: false } },
        legend: LEGEND_RIGHT,
        tooltip: { format: { value: value => Number(value).toLocaleString() } },
      }}
    />
  </ChartFigure>
)

/* a finding's severity cell: high in the brief's warning red, medium in the amber of its flags, low plain */
const flagOf = (severity?: Severity): string | undefined => {
  if (severity === 'High') return styles.rowWarn
  if (severity === 'Medium') return demoStyles.rowMedium
  return undefined
}

/* yes and on as a tick, no and off as a cross, standing as tall as the word would (the word is kept for screen
   readers); anything else as it is */
const valueOf = (value: string): ReactNode => {
  const yes = /^(yes|on)$/i.test(value)
  if (!yes && !/^(no|off)$/i.test(value)) return value
  return (
    <span className={demoStyles.mark} data-mark={yes ? 'yes' : 'no'} role='img' aria-label={value}>
      {yes ? <CheckLineIcon /> : <CloseLineIcon />}
    </span>
  )
}

const Tile: FunctionComponent<{ tile: DemoTile }> = ({ tile }) => (
  <div className={`surface ${styles.metricSurface}`}>
    <div>
      <div className={styles.tileValue} data-value-size='md'>
        {valueOf(tile.value)}
      </div>
      <h5>{tile.label}</h5>
      {tile.subtext && <small className={`blk subdued no-margin-bottom ${styles.metricSubtext}`}>{tile.subtext}</small>}
    </div>
  </div>
)

/* a tile's finding as a table row: its label, value and what was found */
const asRows = (tiles: DemoTile[]): DemoRow[] =>
  tiles.map(({ label, value, subtext, severity, remedy }) => ({
    name: label,
    value,
    detail: subtext ?? '',
    severity,
    remedy,
  }))

/* highest severity first; findings with none last */
const SEVERITY_RANK: Record<Severity, number> = { High: 0, Medium: 1, Low: 2 }
const bySeverity = (a: DemoRow, b: DemoRow) =>
  (a.severity ? SEVERITY_RANK[a.severity] : 3) - (b.severity ? SEVERITY_RANK[b.severity] : 3)

/* a finding's row: one with a fix opens, as an accordion, into a row under it with what it puts at risk and the
   steps that put it right */
const FindingRow: FunctionComponent<{ row: DemoRow }> = ({ row }) => {
  const [open, setOpen] = useState(false)
  const { remedy } = row
  return (
    <>
      <tr
        className={remedy ? demoStyles.findingRow : undefined}
        data-open={open || undefined}
        onClick={remedy ? () => setOpen(!open) : undefined}
      >
        <td>{row.name}</td>
        <td>{valueOf(row.value)}</td>
        <td>{row.detail}</td>
        <td className={flagOf(row.severity)}>
          {/* the severity, and (for a finding with a fix) a chevron in its colour that opens it, turning over once
              open */}
          <span className={demoStyles.severityCell}>
            {row.severity ?? '—'}
            {remedy && (
              <button
                type='button'
                className={demoStyles.fixToggle}
                aria-expanded={open}
                aria-label={`How to fix: ${row.name}`}
                title={open ? 'Hide the fix' : 'How to fix it'}
                onClick={event => {
                  event.stopPropagation()
                  setOpen(!open)
                }}
              />
            )}
          </span>
        </td>
      </tr>
      {remedy && open && (
        <tr className={demoStyles.findingFix}>
          <td colSpan={4}>
            <div className={demoStyles.remedyBody}>
              <p>{remedy.risk}</p>
              <h5>Mitigations</h5>
              <ol>
                {remedy.mitigations.map(({ lead, text }) => (
                  <li key={lead}>
                    {lead}: {text}
                  </li>
                ))}
              </ol>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

/* a group's findings in a table, highest severity first, each with its severity as a chip; those with a fix open to
   show it */
const Table: FunctionComponent<{ label: string; columns: [string, string, string]; rows: DemoRow[] }> = ({
  label,
  columns,
  rows,
}) => (
  <div className={`${styles.tableContainer} ${styles.sectionContainer}`}>
    <table className={`${styles.table} ${demoStyles.findingTable}`} aria-label={label}>
      {/* fixed widths, so opening a row's fix doesn't shift the columns */}
      <colgroup>
        <col className={demoStyles.checkColumn} />
        <col className={demoStyles.findingColumn} />
        <col />
        <col className={demoStyles.severityColumn} />
      </colgroup>
      <thead>
        <tr>
          {columns.map(column => (
            <th key={column}>{column}</th>
          ))}
          <th>Severity</th>
        </tr>
      </thead>
      <tbody>
        {[...rows].sort(bySeverity).map(row => (
          <FindingRow key={row.name} row={row} />
        ))}
      </tbody>
    </table>
  </div>
)

/* the summary: how many findings of each severity each group has */
const Summary: FunctionComponent = () => (
  <div className={`${styles.tableContainer} ${styles.sectionContainer}`}>
    <table className={styles.table} aria-label='Summary'>
      <thead>
        <tr>
          <th>Summary</th>
          {SEVERITY_ORDER.map(severity => (
            <th key={severity}>{severity}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {CATEGORIES.map(({ id, title, findings }) => (
          <tr key={title}>
            <td>
              {/* each group scrolls down to its section */}
              <a
                href={`#${id}`}
                onClick={event => {
                  event.preventDefault()
                  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
              >
                {title}
              </a>
            </td>
            {SEVERITY_ORDER.map(severity => {
              const count = countOf(findings, severity)
              return (
                <td key={severity} className={count > 0 ? flagOf(severity) : undefined}>
                  {count}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

/* 75th, 81st, 82nd, 83rd, 11th, 12th, 13th */
const ordinal = (n: number) => {
  const tens = n % 100
  const suffix = tens >= 11 && tens <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'
  return `${n}${suffix}`
}

/* the benchmark: a bar from weakest (red) to strongest (green), in fifths, with a knob where the customer sits
   among all customers (labelled above the bar) and among its industry (labelled below) */
const Benchmark: FunctionComponent<{ industry?: string }> = ({ industry }) => {
  const peers = industry || 'Their industry'
  const knob = (percentile: number, group: string, place: 'above' | 'below') => (
    <div
      className={demoStyles.benchmarkKnob}
      data-place={place}
      ref={el => el?.style.setProperty('--at', `${percentile}%`)}
    >
      {/* labelled as the brief's tooltips are, pointing at the knob */}
      <span className={`tip visible center${place === 'below' ? ' b' : ''}`}>
        <strong>{ordinal(percentile)} percentile</strong> {group}
      </span>
    </div>
  )
  return (
    <div className={`${demoStyles.benchmark} ${styles.sectionContainer}`}>
      <h5 className='no-margin-bottom'>Benchmark</h5>
      <p>
        A stronger security set-up than {BENCHMARK.all}% of all PaperCut customers, and {BENCHMARK.industry}% of{' '}
        {industry ? `${industry} customers` : 'those in their industry'}. Fixing the high-severity findings would move
        them into the top fifth of both.
      </p>
      <div
        className={demoStyles.benchmarkScale}
        role='img'
        aria-label={`Benchmark: ${ordinal(BENCHMARK.all)} percentile of all customers, ${ordinal(
          BENCHMARK.industry
        )} percentile of ${peers}`}
      >
        {knob(BENCHMARK.all, 'All customers', 'above')}
        <div className={demoStyles.benchmarkBar} />
        {knob(BENCHMARK.industry, peers, 'below')}
      </div>
    </div>
  )
}

const Section: FunctionComponent<{ title: string; summary: string; icon: ReactNode; testId: string }> = ({
  title,
  summary,
  icon,
  testId,
  children,
}) => (
  <div className={styles.sectionWrapper} id={testId} data-testid={testId}>
    <header className={styles.sectionHeader}>
      <figure className='angular'>
        <i className='icon thirtytwo flex-noshrink'>{icon}</i>
      </figure>
      <h4 className={styles.sectionTitle}>{title}</h4>
    </header>
    <p>{summary}</p>
    {children}
  </div>
)

const TileGrid: FunctionComponent<{ tiles: DemoTile[] }> = ({ tiles }) => (
  <div className={`${styles.metricGrid} ${styles.threeColumnMetricGrid}`}>
    {tiles.map(tile => (
      <Tile key={tile.label} tile={tile} />
    ))}
  </div>
)

const DonutGrid: FunctionComponent<{ donuts: DemoDonut[] }> = ({ donuts }) => (
  <div className={`${demoStyles.chartGrid} ${styles.sectionContainer}`}>
    {donuts.map(donut => (
      <Donut key={donut.key} donut={donut} />
    ))}
  </div>
)

/* charts side by side, each filling its share of the row */
const ChartGrid: FunctionComponent = ({ children }) => (
  <div className={`${demoStyles.chartGrid} ${styles.sectionContainer}`}>{children}</div>
)

export const SecurityDemoBrief: FunctionComponent<Props> = ({ teamID, host }) => {
  const { profile } = useCustomerProfile(teamID, host.id)
  // made up, like everything else here: as if generated this morning
  const generatedAt = new Date(new Date().setHours(8, 30, 0, 0)).toISOString()

  return (
    <BriefShell
      teamID={teamID}
      host={host}
      briefType='security-demo'
      title='Security (DEMO)'
      generatedAt={generatedAt}
      industry={profile?.industry}
      renderOverview={() => (
        <>
          {/* in place of the headline stub: the findings by severity, and how they fall across the groups */}
          <Section
            title='Highlights'
            summary='Medium and high severity findings should be taken seriously, investigated and fixed. Each group below shows where they are, and why they matter.'
            icon={<HighlightsSectionIcon />}
            testId='security-demo-summary'
          >
            <SeverityChart />
            <Benchmark industry={profile?.industry} />
            <Summary />
          </Section>
          <KeyInsights insights={INSIGHTS} createdAt={generatedAt} />
        </>
      )}
    >
      {() => (
        <>
          <Section
            title='Version and platform'
            summary='Whether the customer is running software and hardware that still gets security fixes.'
            icon={<SecurityOfficerIcon />}
            testId='security-demo-exposure'
          >
            <TileGrid tiles={EXPOSURE_TILES} />
            <Table
              label='Version and platform'
              columns={['Check', 'Finding', 'Detail']}
              rows={[...asRows(EXPOSURE_TILES), ...PLATFORM]}
            />
          </Section>
          <Section
            title='Access control'
            summary='Who can print, release and administer, and how strongly they prove who they are.'
            icon={<LockLineIcon />}
            testId='security-demo-access'
          >
            <TileGrid tiles={ACCESS_TILES} />
            <ChartGrid>
              <SignInChart />
            </ChartGrid>
            <Table label='Access control' columns={['Check', 'Finding', 'Detail']} rows={asRows(ACCESS_TILES)} />
          </Section>
          <Section
            title='Document protection'
            summary='Whether printed and scanned documents are kept from the wrong people, and whether there is an audit trail.'
            icon={<PaperDocumentIcon />}
            testId='security-demo-documents'
          >
            <DonutGrid donuts={DOCUMENT_DONUTS} />
            <ChartGrid>
              <AbandonedTrendChart />
            </ChartGrid>
            <Table label='Document controls' columns={['Control', 'Status', 'Detail']} rows={DOCUMENT_CONTROLS} />
          </Section>
        </>
      )}
    </BriefShell>
  )
}
